/**
 * React Query hooks for article brief data fetching and mutations
 *
 * This file provides type-safe React Query hooks for article brief operations.
 * React Query handles caching, refetching, and optimistic updates automatically.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { ArticleBriefService } from '../../../utils/services/articleBriefService';
import { broadcastBriefsChanged, queryKeys } from '../../../lib/react-query';
import { API_URL } from '../../../config';
import type { ArticleBrief } from '../../../types/article-brief';

/**
 * Fetch all article briefs with automatic caching
 *
 * Features:
 * - Automatically caches article briefs
 * - Refetches when window regains focus
 * - Handles loading and error states
 * - Refetches on mount to ensure fresh data
 *
 * @returns React Query result with article briefs array
 *
 * @example
 * ```typescript
 * const { data: briefs = [], isLoading, error } = useArticleBriefs();
 *
 * if (isLoading) return <Loading />;
 * if (error) return <Error error={error} />;
 * return <BriefList briefs={briefs} />;
 * ```
 */
export function useArticleBriefs() {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: queryKeys.brief.list(),
    queryFn: async () => {
      // Create service instance with auth
      const service = new ArticleBriefService(API_URL, getToken);
      const response = await service.getAll();

      // Handle ErrorHandlingResponse type from service
      if ('success' in response && !response.success) {
        throw new Error(response.error || 'Failed to load article briefs');
      }

      return response.data || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - shorter than default for list freshness
    refetchOnMount: false, // Use cached data when available to reduce API calls
    refetchOnWindowFocus: true, // Refresh when user returns to tab (catches background updates)
  });
}

/**
 * Fetch a single article brief by ID
 *
 * @param briefId - Article brief ID
 * @returns React Query result with article brief data
 *
 * @example
 * ```typescript
 * const { data: brief, isLoading } = useArticleBrief(briefId);
 * ```
 */
export function useArticleBrief(briefId: string | undefined) {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: queryKeys.brief.detail(briefId!),
    queryFn: async () => {
      const service = new ArticleBriefService(API_URL, getToken);
      return service.getById(briefId!);
    },
    enabled: !!briefId,
    staleTime: 5 * 60 * 1000, // 5 minutes for individual briefs
  });
}

/**
 * Check for related data before deletion
 *
 * This mutation checks if a brief has related articles, outlines, etc.
 * Use this to show a detailed confirmation dialog.
 *
 * @returns React Query mutation for checking related data
 *
 * @example
 * ```typescript
 * const checkRelated = useCheckRelatedData();
 *
 * const handleDelete = async (briefId: string) => {
 *   const relatedData = await checkRelated.mutateAsync(briefId);
 *   if (relatedData.hasRelatedData) {
 *     // Show warning dialog
 *   }
 * };
 * ```
 */
export function useCheckRelatedData() {
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async (briefId: string) => {
      const service = new ArticleBriefService(API_URL, getToken);
      return service.checkRelatedData(briefId);
    },
  });
}

/**
 * Delete article brief mutation
 *
 * Features:
 * - Automatically invalidates related queries after deletion
 * - Handles cascade deletion of related data
 * - Updates cache immediately
 *
 * @returns React Query mutation for deleting article briefs
 *
 * @example
 * ```typescript
 * const deleteBrief = useDeleteArticleBrief();
 *
 * const handleConfirmDelete = async () => {
 *   try {
 *     await deleteBrief.mutateAsync(briefId);
 *     toast.success('Brief deleted');
 *   } catch (error) {
 *     toast.error('Failed to delete brief');
 *   }
 * };
 * ```
 */
export function useDeleteArticleBrief() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (briefId: string) => {
      const service = new ArticleBriefService(API_URL, getToken);
      return service.delete(briefId);
    },
    onSuccess: (_, briefId) => {
      // Invalidate and refetch brief list
      queryClient.invalidateQueries({
        queryKey: queryKeys.brief.list(),
      });

      // Remove deleted brief from cache
      queryClient.removeQueries({
        queryKey: queryKeys.brief.detail(briefId),
      });

      // Also invalidate related queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.outline.byBrief(briefId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.article.byBrief(briefId),
      });

      broadcastBriefsChanged();
    },
    onError: (error) => {
      console.error('Failed to delete article brief:', error);
    },
  });
}

/**
 * Optimistically update a brief in the cache
 *
 * Use this to update the cache immediately without waiting for server response.
 *
 * @returns Function to update brief in cache
 *
 * @example
 * ```typescript
 * const updateBriefCache = useUpdateBriefCache();
 *
 * const handleUpdate = (briefId: string, updates: Partial<ArticleBrief>) => {
 *   updateBriefCache(briefId, updates);
 * };
 * ```
 */
export function useUpdateBriefCache() {
  const queryClient = useQueryClient();

  return (briefId: string, updates: Partial<ArticleBrief>) => {
    // Update individual brief cache
    queryClient.setQueryData(
      queryKeys.brief.detail(briefId),
      (old: ArticleBrief | undefined) => {
        if (!old) return old;
        return { ...old, ...updates };
      }
    );

    // Update brief in list cache
    queryClient.setQueryData(
      queryKeys.brief.list(),
      (old: ArticleBrief[] | undefined) => {
        if (!old) return old;
        return old.map(brief =>
          brief.id === briefId ? { ...brief, ...updates } : brief
        );
      }
    );
  };
}

/**
 * Invalidate all brief queries
 *
 * Use this to force a refetch of all brief data.
 *
 * @returns Function to invalidate brief queries
 *
 * @example
 * ```typescript
 * const invalidateBriefs = useInvalidateBriefs();
 *
 * const handleRefresh = () => {
 *   invalidateBriefs();
 * };
 * ```
 */
export function useInvalidateBriefs() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.brief.all,
    });
  };
}

/**
 * Create article brief mutation
 *
 * Features:
 * - Automatically invalidates brief list after creation
 * - Adds new brief to cache immediately
 * - Returns the created brief for navigation
 *
 * @returns React Query mutation for creating article briefs
 *
 * @example
 * ```typescript
 * const createBrief = useCreateArticleBrief();
 *
 * const handleCreate = async (data: ArticleBriefFormData) => {
 *   try {
 *     const newBrief = await createBrief.mutateAsync(data);
 *     navigate(`/brief/${newBrief.id}`);
 *   } catch (error) {
 *     toast.error('Failed to create brief');
 *   }
 * };
 * ```
 */
export function useCreateArticleBrief() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      metadata?: Record<string, unknown>,
      name: string;
      title: string;
      task: string;
      description?: string;
      urls?: string[];
    }) => {
      const service = new ArticleBriefService(API_URL, getToken);
      return service.create(data);
    },
    onSuccess: (newBrief) => {
      // Add to list cache
      queryClient.setQueryData(
        queryKeys.brief.list(),
        (old: ArticleBrief[] | undefined) => {
          if (!old) return [newBrief];
          return [...old, newBrief];
        }
      );

      // Add to detail cache
      queryClient.setQueryData(
        queryKeys.brief.detail(newBrief.id),
        newBrief
      );

      // Invalidate list to ensure consistency
      queryClient.invalidateQueries({
        queryKey: queryKeys.brief.list(),
      });

      broadcastBriefsChanged();
    },
    onError: (error) => {
      console.error('Failed to create article brief:', error);
    },
  });
}

/**
 * Update article brief mutation
 *
 * Features:
 * - Automatically updates caches after mutation
 * - Optimistically updates UI before server response
 * - Rolls back on error
 *
 * @returns React Query mutation for updating article briefs
 *
 * @example
 * ```typescript
 * const updateBrief = useUpdateArticleBrief();
 *
 * const handleUpdate = async (briefId: string, data: Partial<ArticleBrief>) => {
 *   try {
 *     await updateBrief.mutateAsync({ briefId, data });
 *     toast.success('Brief updated');
 *   } catch (error) {
 *     toast.error('Failed to update brief');
 *   }
 * };
 * ```
 */
export function useUpdateArticleBrief() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ briefId, data }: { briefId: string; data: Partial<ArticleBrief> }) => {
      const service = new ArticleBriefService(API_URL, getToken);
      return service.update(briefId, data);
    },
    onSuccess: (updatedBrief, { briefId }) => {
      // Update detail cache
      queryClient.setQueryData(
        queryKeys.brief.detail(briefId),
        updatedBrief
      );

      // Update in list cache
      queryClient.setQueryData(
        queryKeys.brief.list(),
        (old: ArticleBrief[] | undefined) => {
          if (!old) return [updatedBrief];
          return old.map(brief =>
            brief.id === briefId ? updatedBrief : brief
          );
        }
      );

      // Invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey: queryKeys.brief.detail(briefId),
      });

      broadcastBriefsChanged();
    },
    onError: (error) => {
      console.error('Failed to update article brief:', error);
    },
  });
}
